import React, { useMemo, useState, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ITEMS } from '../data/erpData';
import { useLang } from '../i18n/LangContext';
import GridToolbar from '../components/GridToolbar';
import '../styles/DataGrid.css';

const ItemMasterPage = () => {
  const gridRef = useRef();
  const { t, fmtCurrency, lang } = useLang();
  const [rowData, setRowData] = useState(ITEMS.map((item, i) => ({ ...item, _id: i })));

  const groupLabel = (g) => {
    if (lang === 'ja') {
      if (g === '메모리') return 'メモリ';
      if (g === '비메모리') return '非メモリ';
      if (g === 'SiP 외') return 'SiP他';
      return g;
    }
    return g;
  };

  const columnDefs = useMemo(() => [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 48, pinned: 'left', lockPosition: true, suppressMenu: true, resizable: false },
    { field: 'code', headerName: t.materials.code, width: 120, pinned: 'left', editable: true },
    { field: 'name', headerName: t.materials.name, width: 250, editable: true },
    { field: 'group', headerName: t.materials.group, width: 110, editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['메모리', '비메모리', 'SiP 외'] },
      valueFormatter: params => groupLabel(params.value),
      cellStyle: params => {
        const colors = { '메모리': '#dbeafe', '비메모리': '#f3e8ff', 'SiP 외': '#ccfbf1' };
        return { backgroundColor: colors[params.value] || 'transparent', fontWeight: 500 };
      }
    },
    { field: 'cls', headerName: lang === 'ko' ? '품목분류' : '品目分類', width: 110, editable: true },
    { field: 'type', headerName: lang === 'ko' ? '품목타입' : '品目タイプ', width: 90, editable: true,
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['P', 'M'] },
    },
    { field: 'alt', headerName: lang === 'ko' ? '대체타입' : '代替タイプ', width: 90, editable: true,
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['P', 'M'] },
    },
    { field: 'cost', headerName: lang === 'ko' ? '구매원가' : '購入原価', width: 120, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value ? fmtCurrency(params.value) : '-',
      valueParser: params => Number(params.newValue) || 0,
    },
    { field: 'mfg', headerName: lang === 'ko' ? '제조원가' : '製造原価', width: 120, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value > 0 ? fmtCurrency(params.value) : '-',
      valueParser: params => Number(params.newValue) || 0,
    },
    { field: 'altLt', headerName: lang === 'ko' ? '대체리드타임' : '代替リードタイム', width: 130, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value > 0 ? `${params.value}${t.dashboard.day}` : '-',
      valueParser: params => Number(params.newValue) || 0,
    },
  ], [t, lang, fmtCurrency]);

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);

  const newRowTemplate = useCallback((data) => ({
    _id: Date.now(), code: `P${String(data.length + 1).padStart(6, '0')}`,
    name: '', group: '메모리', cls: '', type: 'P', alt: 'P', cost: 0, mfg: 0, altLt: 0,
  }), []);

  const groupCount = useMemo(() => {
    const counts = {};
    rowData.forEach(r => { counts[r.group] = (counts[r.group] || 0) + 1; });
    return counts;
  }, [rowData]);

  return (
    <div className="data-page">
      <div className="data-page-header">
        <div className="data-page-title-area">
          <h2 className="data-page-title">{t.materials.title}</h2>
          <span className="data-page-count">
            {rowData.length}{lang === 'ko' ? '개 품목' : '品目'}
          </span>
        </div>
        <div className="data-page-badges">
          {Object.entries(groupCount).map(([group, count]) => (
            <span key={group} className={`badge badge-${group === '메모리' ? 'blue' : group === '비메모리' ? 'purple' : 'teal'}`}>
              {groupLabel(group)}: {count}
            </span>
          ))}
        </div>
      </div>
      <GridToolbar gridRef={gridRef} rowData={rowData} setRowData={setRowData} newRowTemplate={newRowTemplate} entityName={lang === 'ko' ? '품목' : '品目'} />
      <div className="ag-theme-alpine data-grid-wrapper">
        <AgGridReact ref={gridRef} rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
          animateRows={true} rowSelection="multiple" enableCellTextSelection={true} undoRedoCellEditing={true}
          undoRedoCellEditingLimit={20} singleClickEdit={false} stopEditingWhenCellsLoseFocus={true}
          suppressRowClickSelection={true} enableRangeSelection={false}
        />
      </div>
    </div>
  );
};

export default ItemMasterPage;
